import { Box, ListItemIcon } from "@material-ui/core";
import SettingsIcon from "@material-ui/icons/Settings";
import Grid from "@material-ui/core/Grid";
import GetAppIcon from "@material-ui/icons/GetApp";
import ListItemText from "@material-ui/core/ListItemText/ListItemText";
import { makeStyles } from "@material-ui/styles";
import React, { useContext, useEffect, useRef, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { animated, useSpring } from "react-spring";
import RequestsPagePathParams from "../../../api/models/test-request/RequestsPagePathParams";
import TestSpecificationDTO from "../../../api/models/test-request/TestSpecificationDTO";
import TestPlanResponse from "../../../api/models/test-series/TestPlanResponse";
import AttachmentApi from "../../../api/services/AttachmentApi";
import TestRequestApi from "../../../api/services/TestRequestApi";
import TestSeriesApi from "../../../api/services/TestSeriesApi";
import CustomProgress from "../../../components/Alert/CustomProgress";
import CustomButton from "../../../components/Button/CustomButton";
import ConfirmationDialogContext from "../../../components/Dialog/ConfirmationDialog/ConfirmationDialogContext";
import ParameterListDialog from "../../../components/Dialog/ParameterListDialog/ParameterListDialog";
import SimpleTextDialog from "../../../components/Dialog/SimpleTextDialog";
import GalleryButton from "../../../components/Gallery/button/GalleryButton";
import ErrorSnackbarContext from "../../../contexts/ErrorSnackbarContext";
import TestPlanPermissionContext from "../../../contexts/TestPlanPermissionContext";
import { TestRequestContext } from "../../../contexts/TestRequestContext";
import TestRequestPermissionContext from "../../../contexts/TestRequestPermissionContext";
import { TestSeriesContext, TestSeriesTabType } from "../../../contexts/TestSeriesContext";
import TestRequestHelper from "../../../helper/TestRequestHelper";
import useEnv from "../../../hooks/env";
import { tesaDarkRedError } from "../../../styles/colors";
import { fadeIn } from "../../../styles/springs";
import CustomFooterGraphic from "../CustomFooterGraphic";
import SampleSuggestionField from "../SampleSuggestionField";
import AnalyzeHistoricalSettingDialog from "./AnalyzeHistoricalSettingsDialog";
import { AnalyzeHistoricalFormType } from "./AnalyzeHistoricalForm";
import TestSeriesTabbedDialog from "./TabbedDialog/TestSeriesTabbedDialog";
import TestSeriesMethodDialog from "./TestSeriesMethodDialog";
import TestSeriesOverviewTable from "./TestSeriesOverviewTable";
import TestSpecificationDialog from "./TestSpecificationDialog";
import { MinimalSampleReference, TestSpecification } from "./testTab.models";
import AnalyzeHistoricalResultsDialog from "./AnalyzeHistoricalResultsDialog";
import useQueryParams from "../../../hooks/useQueryParams.hook";
import TestSeriesSearchResult from "../../../api/models/test-series/TestSeriesSearchResult";
import TestInformationApi from "../../../api/services/TestInformationApi";
import ReusedTestSeriesSearchResult from "../../../api/models/test-series/ReusedTestSeriesSearchResult";
// eslint-disable-next-line max-len
import TestSeriesSearchResultRegularOrReused from "../../../api/models/test-series/TestSeriesSearchResultRegularOrReused";
import ReusedTestSeriesApi from "../../../api/services/ReusedTestSeriesApi";
import downloadFile from "../../../functions/downloadFile.function";
import CustomMenu from "../../../components/Menu/CustomMenu";
import { StyledMenuItem } from "../../../components/Menu/StyledMenu";
import MatrixExportConfigurationDialog from "./MatrixExportConfigurationDialog/MatrixExportConfigurationDialog";
import TestSeriesExcelExportDialog from "./TestSeriesExcelExportDialog/TestSeriesExcelExportDialog";

const useStyles = makeStyles({
  buttonContainer: {
    "& > * + *": {
      marginLeft: "2em",
    },
  },
});

type TestSeries<IsReused> = IsReused extends true ? ReusedTestSeriesSearchResult : TestSeriesSearchResult;
type TabbedDialogOpenHandler = (
  testSeriesId: number,
  testSeriesVersion: number,
  isReused: boolean,
  originRequestId?: string,
) => void;
type TabbedDialogHandlerResolver<HandlerType extends (...args: any[]) => void> = (
  assertHasTestCases: (id?: number) => boolean,
  tab: TestSeriesTabType,
) => HandlerType;

const DEFAULT_TAB = TestSeriesTabType.TestSeriesConfigurator;

function assertPropertyTruthy<O extends object, K extends keyof O>(obj: O, key: K): boolean {
  return !!obj[key];
}

function getTestSeriesById(id: number, testSeries: TestSeriesSearchResultRegularOrReused[]) {
  return testSeries.find((ts) => ts.id === id) || null;
}

function getTestSeriesByOrdinal(
  ordinal: number,
  testSeries: TestSeriesSearchResultRegularOrReused[],
): TestSeriesSearchResultRegularOrReused | null {
  return testSeries[ordinal - 1] || null;
}

function isTestSeriesTabType(tabType: number | null): tabType is TestSeriesTabType {
  return tabType != null && Object.values(TestSeriesTabType).includes(tabType);
}

function defaultInvalidTab(tabFromParams: number, defaultTab: TestSeriesTabType) {
  if (isTestSeriesTabType(tabFromParams)) return tabFromParams;
  return defaultTab;
}

function openDeepLinkedDialog(deepLinkedDialogParams: {
  searchParams: URLSearchParams;
  testPlans: TestPlanResponse[];
  reusedTestSeries: ReusedTestSeriesSearchResult[];
  handlerResolver: TabbedDialogHandlerResolver<TabbedDialogOpenHandler>;
  resetSearchParams: () => void;
}) {
  const { searchParams, testPlans, reusedTestSeries, handlerResolver, resetSearchParams } = deepLinkedDialogParams;
  const testSeriesId = parseInt(searchParams.get("testSeriesId") || "", 10) || null;
  const testSeriesNo = parseInt(searchParams.get("testSeriesNo") || "", 10) || null;
  const isReused = searchParams.get("reused") === "1";
  const testSeries = isReused ? reusedTestSeries : testPlans.flatMap((tp) => tp.testSeriesSearchResults);
  let tabType = parseInt(searchParams.get("tab") || "", 10) ?? null;
  let found: TestSeriesSearchResultRegularOrReused | null;
  if (tabType != null) {
    tabType = defaultInvalidTab(tabType, DEFAULT_TAB);
    found = testSeriesNo
      ? getTestSeriesByOrdinal(testSeriesNo, testSeries)
      : testSeriesId
      ? getTestSeriesById(testSeriesId, testSeries)
      : null;
    if (found) {
      const version = isReused ? 1 : (found as TestSeriesSearchResult).version;
      const originRequestId = isReused ? (found as ReusedTestSeriesSearchResult).requestId : undefined;
      handlerResolver(() => !!found?.testCasesCount, tabType)(found.id, version, isReused, originRequestId);
      return;
    }
  }
  resetSearchParams();
}

const TestTab: React.FunctionComponent = () => {
  const { api } = useEnv();
  const attachmentApi = new AttachmentApi(api);
  const reusedTestSeriesApi = new ReusedTestSeriesApi(api);
  const testInformationApi = new TestInformationApi(api);
  const testRequestApi = new TestRequestApi(api);
  const testRequestHelper = new TestRequestHelper(new TestRequestApi(api));
  const testSeriesApi = new TestSeriesApi(api);
  const [analyzeHistoricalSettings, setAnalyzeHistoricalSettings] = useState<AnalyzeHistoricalFormType | null>(null);
  const [isActiveDownloadAll, setIsActiveDownloadAll] = useState(false);
  const [isAnalyzeHistoricalResultsDialogOpen, setIsAnalyzeHistoricalResultsDialogOpen] = useState(false);
  const [isAnalyzeHistoricalSettingsDialogOpen, setIsAnalyzeHistoricalSettingsDialogOpen] = useState(false);
  const [isMatrixExportConfigurationDialogOpen, setIsMatrixExportConfigurationDialogOpen] = useState(false);
  const [isExcelExportDialogOpen, setIsExcelExportDialogOpen] = useState(false);

  const {
    isMethodDialogOpen,
    isSpecificationDialogOpen,
    setIsMethodDialogOpen,
    setIsSpecificationDialogOpen,
    regainConfig,
    updateRegainConfig,
    parameterListDialogState,
    textDialog,
    resetSelectedTestSeriesObject,
    updateOpenTabValue,
    updateSelectedTestSeriesId,
    updateSelectedTestSeriesHasTestCases,
    updateTestSeriesCurrentVersion,
    updateTestSeriesIsReused,
    updateOriginRequestId,
  } = useContext(TestSeriesContext);
  const { testRequestObj } = useContext(TestRequestContext);
  const { showConfirmationDialog } = useContext(ConfirmationDialogContext);
  const { handleHttpError, showSnackbar } = useContext(ErrorSnackbarContext);
  const {
    accessTestPlan,
    setAllSamplesReceived,
    updatePermissionState: updateTestRequestPermissionState,
  } = useContext(TestRequestPermissionContext);
  const { analyzeHistoricalData, addTestSeries, downloadResults, updatePermissionState } =
    useContext(TestPlanPermissionContext);
  const { testPlans, setTestPlans } = useContext(TestSeriesContext);

  const [isDownloadingResults, setIsDownloadingResults] = useState(false);
  const [isTabbedDialogOpen, setIsTabbedDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [productSampleReferences, setProductSampleReferences] = useState<MinimalSampleReference[]>([]);
  const [reusedTestSeries, setReusedTestSeries] = useState<ReusedTestSeriesSearchResult[]>([]);
  const [substrateSampleReferences, setSubstrateSampleReferences] = useState<MinimalSampleReference[]>([]);
  const [testSpecifications, setTestSpecifications] = useState<TestSpecificationDTO[]>([]);
  const [matrixExportConfigurationTestSeriesProps, setMatrixExportConfigurationTestSeriesProps] = useState({
    id: 0,
    version: 0,
  });

  const { testRequestId } = useParams<RequestsPagePathParams>();

  const springProps = useSpring(fadeIn);
  const history = useHistory();
  const location = useLocation();
  const searchParams = useQueryParams();
  const isMounted = useRef(false);

  const classes = useStyles();

  const getHasTestCasesById = (testSeriesResults: TestSeriesSearchResultRegularOrReused[]) => {
    return (id?: number) => {
      const testSeries = id != null && testSeriesResults.find((tsr) => tsr.id === id);
      return !!testSeries && assertPropertyTruthy(testSeries, "testCasesCount");
    };
  };

  const updateSearchParams = (newParams: Record<string, string> | ""): void => {
    const oldParams = Object.fromEntries(searchParams.entries());
    const newLocation = {
      ...location,
      search: newParams && `?${new URLSearchParams(Object.assign(oldParams, newParams))}`,
    };
    history.replace(newLocation);
  };

  const getOpenHandler =
    (assertHasTestCases: (id?: number) => boolean, tab: TestSeriesTabType) =>
    (id: number, version: number, isReused: boolean, originRequestId?: string) => {
      updateOriginRequestId(originRequestId || "");
      updateSelectedTestSeriesId(id);
      updateTestSeriesCurrentVersion(version);
      updateTestSeriesIsReused(isReused);
      const hasTestCases = assertHasTestCases(id);
      updateSelectedTestSeriesHasTestCases(hasTestCases);
      updateOpenTabValue(tab);
      updateSearchParams({ testSeriesId: String(id), tab: String(tab), reused: isReused ? "1" : "0" });
      setIsTabbedDialogOpen(true);
    };

  const initialize = (openDialog: boolean = false): void => {
    setLoading(true);
    updatePermissionState()
      .then(updateTestRequestPermissionState)
      .then(() => testInformationApi.fetchTestInformation(testRequestId))
      .then(({ data: testInfo }) => {
        const { testPlans: loadedTestPlans, reusedTestSeries: loadedReusedTestSeries } = testInfo;
        setIsActiveDownloadAll(testInfo.attachmentsAvailable);
        setProductSampleReferences(testInfo.productSamples);
        setReusedTestSeries(loadedReusedTestSeries);
        setSubstrateSampleReferences(testInfo.substrateSamples);
        setTestPlans(loadedTestPlans);
        setTestSpecifications(testInfo.specifications);
        setAnalyzeHistoricalSettings({
          productSamples: testInfo.productSamples,
          substrateSamples: testInfo.substrateSamples,
          testMethods: [],
        });
        if (openDialog) {
          openDeepLinkedDialog({
            reusedTestSeries,
            searchParams,
            testPlans,
            handlerResolver: getOpenHandler,
            resetSearchParams: () => updateSearchParams(""),
          });
        }
      })
      .catch(handleHttpError)
      .finally(() => setLoading(false));
  };

  const handleAllSamplesReceived = () => {
    showConfirmationDialog({
      headline: "All Samples Received?",
      text: `This will set the date of receipt of every requester delivered substrate sample 
        and product sample which does not yet have a date set to the current day.`,
      confirmText: "Continue",
    })
      .then(() => {
        testRequestHelper
          .setAllSamplesReceived(testRequestId)
          .then(() => {
            showSnackbar("All samples set to received.", "success");
          })
          .then(updateTestRequestPermissionState)
          .catch(handleHttpError);
      })
      .catch(() => {
        // ignore cancel
      });
  };

  const handleAnalyzeHistoricalClick = () => {
    setIsAnalyzeHistoricalSettingsDialogOpen(true);
  };

  const handleAnalyzeHistoricalSettingsSubmit = (values: AnalyzeHistoricalFormType) => {
    setAnalyzeHistoricalSettings(values);
    setIsAnalyzeHistoricalResultsDialogOpen(true);
  };

  const onHandleOpenExportConfigMatrix = (isOpen: boolean, id: number, version: number): void => {
    setIsMatrixExportConfigurationDialogOpen(isOpen);
    setMatrixExportConfigurationTestSeriesProps({ id, version });
  };

  const handleDelete = (id: number, name: string, isReused?: boolean) => {
    const regularText = (
      <>
        You are about to delete <b>{name}</b>.
      </>
    );
    const reusedText = (
      <>
        You are about to remove the historical data from this test request <b>{name}</b>.
      </>
    );
    const reusedRequest = () => reusedTestSeriesApi.removeReusedTestSeries(testRequestId, id);
    const regularRequest = () => testSeriesApi.deleteTestSeriesById(id);
    showConfirmationDialog({
      confirmText: isReused ? "Remove" : "Delete",
      confirmColor: tesaDarkRedError,
      text: isReused ? reusedText : regularText,
    })
      .then(() => {
        return isReused
          ? reusedRequest()
              .then(() => initialize())
              .catch(handleHttpError)
          : regularRequest()
              .then(() => initialize())
              .catch(handleHttpError);
      })
      .catch(() => {});
  };

  const handleDuplicate = (id: number) => {
    testSeriesApi
      .duplicateTestSeriesById(id)
      .then(() => {
        initialize();
      })
      .catch(handleHttpError);
  };

  const handleCancel = (id: number, name: string) => {
    showConfirmationDialog({
      confirmText: "Cancel Test Series",
      confirmColor: tesaDarkRedError,
      text: (
        <>
          You are about to cancel <b>{name}</b>.
        </>
      ),
    })
      .then(() => {
        testSeriesApi
          .cancelTestSeriesById(id)
          .then(() => {
            resetSelectedTestSeriesObject();
            initialize();
          })
          .catch(handleHttpError);
      })
      .catch(() => {});
  };

  const handleMoveToDraft = (id: number, name: string) => {
    showConfirmationDialog({
      confirmText: "Discard Values and Set to Draft",
      confirmColor: tesaDarkRedError,
      text: (
        <>
          When you set the test series <b>{name}</b> to draft all test cases and documented values and attachments are
          discarded. Please make sure to download any documented values and attachments first.
        </>
      ),
    })
      .then(() => {
        testSeriesApi
          .moveTestSeriesToDraft(id)
          .then(() => {
            initialize();
          })
          .catch(handleHttpError);
      })
      .catch(() => {
        // ignore cancel
      });
  };

  const handleReopen = (id: number) => {
    testSeriesApi
      .reopenTestSeriesById(id)
      .then(() => {
        resetSelectedTestSeriesObject();
        initialize();
      })
      .catch(handleHttpError);
  };

  const handleTabChange = (tab: TestSeriesTabType) => {
    updateSearchParams({ tab: String(tab) });
    updateOpenTabValue(tab);
  };

  const downloadMergedTestPlanResults = () => {
    setIsDownloadingResults(true);
    testSeriesApi
      .exportAllMergedTestPlansOfTestRequest(testRequestId)
      .then((response) => downloadFile(response, `${testRequestObj.fullTitle}_all_results.xls`))
      .catch(handleHttpError)
      .finally(() => setIsDownloadingResults(false));
  };

  const downloadTestPlanResults = () => {
    testSeriesApi
      .exportAllTestPlansOfTestRequest(testRequestId)
      .then((response) => downloadFile(response, `${testRequestObj.fullTitle}.xls`))
      .catch(handleHttpError);
  };

  const downloadTestPlanPicsAndDocs = (requestId: string): void => {
    testRequestApi
      .getDownloadAllTestPlanAttachmentsClearanceToken(requestId)
      .then(({ data: token }) => attachmentApi.downloadAttachmentsStructured({ accessToken: token }))
      .catch(handleHttpError);
  };

  const createTestSeriesOverviewTable = <IsReused extends boolean, TS extends TestSeries<IsReused>>(
    testSeriesInformation: {
      testSeries: TS[];
      isReused?: IsReused;
    },
    testPlanInformation?: { withoutSpecification?: boolean; testPlanVersionName?: string; testPlanId: number },
  ) => {
    const { testSeries, isReused } = testSeriesInformation;
    const { testPlanId, testPlanVersionName, withoutSpecification } = testPlanInformation || {};
    const hasTestCasesById = getHasTestCasesById(testSeries);
    return (
      <TestSeriesOverviewTable
        data={testSeries}
        isReused={!!isReused}
        key={testPlanId || "reusedTestSeries"}
        onOpenTestConfiguration={getOpenHandler(hasTestCasesById, TestSeriesTabType.TestSeriesConfigurator)}
        onOpenTestOverview={getOpenHandler(hasTestCasesById, TestSeriesTabType.TestTable)}
        onDuplicate={(id: number): void => {
          handleDuplicate(id);
        }}
        onDelete={(d) => {
          handleDelete(d.id, d.testMethod, isReused);
        }}
        onCancel={(d) => {
          handleCancel(d.id, d.testMethod);
        }}
        onReopen={handleReopen}
        onMoveToDraft={(d) => {
          handleMoveToDraft(d.id, d.testMethod);
        }}
        testPlanName={testPlanVersionName || null}
        withoutSpecification={withoutSpecification}
        onOpenMatrixExportConfiguration={onHandleOpenExportConfigMatrix}
      />
    );
  };

  const renderTestSeriesTables = (
    testPlansToRender: TestPlanResponse[],
    reusedTestSeriesToRender: ReusedTestSeriesSearchResult[],
  ) => {
    const tables = testPlansToRender.map((tp) => {
      const { id: testPlanId, testPlanVersionName, testSeriesSearchResults: testSeries, withoutSpecification } = tp;
      return createTestSeriesOverviewTable(
        { testSeries },
        {
          testPlanId,
          testPlanVersionName,
          withoutSpecification,
        },
      );
    });
    if (reusedTestSeriesToRender.length > 0) {
      tables.push(
        createTestSeriesOverviewTable({
          testSeries: reusedTestSeriesToRender,
          isReused: true,
        }),
      );
    }
    return tables;
  };

  useEffect(() => {
    if (!isTabbedDialogOpen && isMounted.current) {
      history.replace(location.pathname);
      initialize();
    }
  }, [isTabbedDialogOpen]);

  useEffect(() => {
    initialize(true);
    isMounted.current = true;
  }, []);

  const customMenuRef = useRef<{ close: () => void }>(null);

  if (!accessTestPlan) {
    return <animated.div style={springProps}>Access denied.</animated.div>;
  }

  return (
    <animated.div style={springProps}>
      <Box mb={3}>
        <SampleSuggestionField
          sampleTypeName="Specification"
          value={testRequestObj?.description?.specifications || null}
        />
        <SampleSuggestionField
          label="Additional information given by the requester"
          sampleTypeName="TestProgram"
          value={testRequestObj?.description?.testProgram || null}
        />
      </Box>
      <Box paddingTop={1}>
        <Box display="flex" flexWrap="wrap">
          {addTestSeries && (
            <>
              <Box marginRight={2}>
                <CustomButton add onClick={() => setIsMethodDialogOpen(true)} text="Create Test Series" />
              </Box>
              <Box marginRight={2} marginBottom={2}>
                <CustomButton add onClick={() => setIsSpecificationDialogOpen(true)} text="Test Specification" />
              </Box>
            </>
          )}
          {setAllSamplesReceived && (
            <Box marginRight={2} marginBottom={2}>
              <CustomButton onClick={handleAllSamplesReceived} text="All Samples Received" />
            </Box>
          )}
          {analyzeHistoricalData && (
            <Box marginRight={2} marginBottom={2}>
              <CustomButton onClick={handleAnalyzeHistoricalClick} text="Analyze Historical Data" />
            </Box>
          )}
        </Box>
        <Box marginTop={4}>
          {loading ? <CustomProgress /> : renderTestSeriesTables(testPlans, reusedTestSeries)}
          <Grid container justify="flex-end" alignItems="center" className={classes.buttonContainer}>
            {downloadResults && (
              <>
                <Grid item alignItems="center">
                  <GalleryButton
                    icon="DOWNLOAD"
                    loading={isDownloadingResults}
                    onClick={() => downloadMergedTestPlanResults()}
                    text="Download All Results"
                  />
                </Grid>
                <Grid item alignItems="center">
                  <CustomMenu ref={customMenuRef}>
                    <StyledMenuItem
                      onClick={(): void => {
                        customMenuRef.current?.close();
                        setIsExcelExportDialogOpen(true);
                      }}
                    >
                      <ListItemIcon>
                        <SettingsIcon />
                      </ListItemIcon>
                      <ListItemText primary="Selective Excel Export" />
                    </StyledMenuItem>
                    <StyledMenuItem
                      onClick={(): void => {
                        customMenuRef.current?.close();
                        setIsMatrixExportConfigurationDialogOpen(true);
                        setMatrixExportConfigurationTestSeriesProps({
                          id: 0,
                          version: 0,
                        });
                      }}
                    >
                      <ListItemIcon>
                        <SettingsIcon />
                      </ListItemIcon>
                      <ListItemText primary="Configure Test Request Export" />
                    </StyledMenuItem>
                    <StyledMenuItem
                      onClick={(): void => {
                        customMenuRef.current?.close();
                        downloadTestPlanResults();
                      }}
                    >
                      <ListItemIcon>
                        <GetAppIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary="Download Results (old format)" />
                    </StyledMenuItem>
                    <StyledMenuItem
                      disabled={!isActiveDownloadAll}
                      onClick={(): void => {
                        customMenuRef.current?.close();
                        downloadTestPlanPicsAndDocs(testRequestId);
                      }}
                    >
                      <ListItemIcon>
                        <GetAppIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary="Download Pictures and Documents" />
                    </StyledMenuItem>
                  </CustomMenu>
                </Grid>
              </>
            )}
          </Grid>
        </Box>
        <SimpleTextDialog {...textDialog} />
        <ParameterListDialog {...parameterListDialogState} />
        <TestSeriesMethodDialog
          open={isMethodDialogOpen}
          onClose={(formComplete): void => {
            setIsMethodDialogOpen(false);
            if (formComplete) {
              if (regainConfig) {
                updateSelectedTestSeriesId(undefined);
                updateOpenTabValue(TestSeriesTabType.TestSeriesConfigurator);
              } else {
                setIsTabbedDialogOpen(true);
              }
            } else {
              updateRegainConfig(null);
            }
          }}
        />
        <TestSeriesTabbedDialog
          open={isTabbedDialogOpen}
          onClose={() => {
            setIsTabbedDialogOpen(false);
            updateSelectedTestSeriesId(undefined);
          }}
          loadDataInParent={(): void => {
            initialize();
          }}
          onSevereError={() => setIsTabbedDialogOpen(false)}
          onTabChange={handleTabChange}
          historicalSearchSettings={analyzeHistoricalSettings}
        />
        <TestSpecificationDialog
          open={isSpecificationDialogOpen}
          onClose={() => {
            setIsSpecificationDialogOpen(false);
          }}
          onSelected={(newTestSpecifications: TestSpecification[]) => {
            setIsSpecificationDialogOpen(false);
            setLoading(true);
            testRequestHelper
              .addTestSpecifications(testRequestId, newTestSpecifications)
              .then(() => initialize())
              .catch((e) => {
                handleHttpError(e);
              })
              .finally(() => setLoading(false));
          }}
          specifications={testSpecifications}
        />
        <AnalyzeHistoricalSettingDialog
          open={isAnalyzeHistoricalSettingsDialogOpen}
          onConfirm={handleAnalyzeHistoricalSettingsSubmit}
          onClose={() => setIsAnalyzeHistoricalSettingsDialogOpen(false)}
          productSamples={productSampleReferences}
          settingsCached={analyzeHistoricalSettings}
          substrateSamples={substrateSampleReferences}
          testSpecifications={testSpecifications}
        />
        <AnalyzeHistoricalResultsDialog
          onChangeSettings={() => setIsAnalyzeHistoricalSettingsDialogOpen(true)}
          onClose={(doReload: boolean = false) => {
            setIsAnalyzeHistoricalResultsDialogOpen(false);
            if (doReload) {
              initialize();
            }
          }}
          open={isAnalyzeHistoricalResultsDialogOpen}
          settingsCached={analyzeHistoricalSettings}
        />
        {isMatrixExportConfigurationDialogOpen && (
          <MatrixExportConfigurationDialog
            open={isMatrixExportConfigurationDialogOpen}
            handleClose={(): void => {
              setIsMatrixExportConfigurationDialogOpen(false);
            }}
            testRequestId={testRequestId}
            testSeriesId={matrixExportConfigurationTestSeriesProps.id}
            testVersion={matrixExportConfigurationTestSeriesProps.version}
          />
        )}
        {isExcelExportDialogOpen && (
          <TestSeriesExcelExportDialog
            open={isExcelExportDialogOpen}
            testRequest={testRequestObj}
            handleClose={() => {
              setIsExcelExportDialogOpen(false);
            }}
          />
        )}
      </Box>
      <CustomFooterGraphic />
    </animated.div>
  );
};

export default TestTab;
